import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import MessageInput from './MessageInput';
import CorrectionResult from './CorrectionResult';
import AppTitle from './AppTitle';

const ChatArea = ({
  chat,
  onAddMessage,
  onUpdateChatTitle,
  onTextProcessing,
  onOpenCreditPanel,
  otjUser,
  onToggleSidebar
}) => {
  const ref = useRef(null);
  const { loginWithRedirect } = useAuth0();

  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chat]);

  // 메시지 렌더링 함수
  const renderMessage = (msg) => {
    console.log('[ChatArea] renderMessage 호출:', msg);
    
    const isUser = msg.type === 'user';
    const isError = msg.type === 'error';
    const isLoading = msg.type === 'loading';
    const isWaiting = msg.type === 'waiting';
    const isCorrectionResult = msg.type === 'correction_result';
    const isCreditShortage = msg.type === 'credit_shortage';
    const isCreditExhausted = msg.type === 'credit_exhausted';

    console.log('[ChatArea] 메시지 타입:', { isUser, isError, isLoading, isWaiting, isCorrectionResult, isCreditShortage, isCreditExhausted });

    // 교정/교열 결과는 특별한 컴포넌트로 렌더링
    if (isCorrectionResult) {
      console.log('[ChatArea] CorrectionResult 렌더링:', {
        data: msg.data,
        mode: msg.mode,
        originalText: msg.originalText
      });
      return (
        <div key={msg.id} className="w-full max-w-4xl mx-auto mb-6">
          <CorrectionResult 
            data={msg.data} 
            mode={msg.mode} 
            originalText={msg.originalText}
            isCached={msg.isCached}
          />
        </div>
      );
    }

    // 크레딧 부족 안내는 특별한 UI로 렌더링
    if (isCreditShortage) {
      return (
        <div key={msg.id} className="w-full max-w-2xl mx-auto mb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="material-icons text-blue-600">account_circle</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  크레딧이 부족합니다
                </h3>
                <div className="text-sm text-gray-600 mb-4">
                  <p className="mb-2">
                    필요한 크레딧: <span className="font-semibold text-red-600">{msg.requiredCredits}</span>
                  </p>
                  <p className="mb-3">
                    보유 크레딧: <span className="font-semibold text-gray-800">{msg.currentCredits}</span>
                  </p>
                  <p className="text-blue-700 font-medium">
                    💎 로그인하시면 10,000 크레딧을 드려요!
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => loginWithRedirect()}
                    className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <span className="material-icons mr-2">login</span>
                    로그인하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // 크레딧 소진 안내는 특별한 UI로 렌더링
    if (isCreditExhausted) {
      return (
        <div key={msg.id} className="w-full max-w-2xl mx-auto mb-6">
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <span className="material-icons text-red-600">error</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  크레딧이 모두 소진되었습니다
                </h3>
                <div className="text-sm text-gray-600 mb-4">
                  <p className="mb-3">
                    보유 크레딧: <span className="font-semibold text-red-600">0</span>
                  </p>
                  <p className="text-red-700 font-medium mb-2">
                    💳 크레딧을 추가하시면 계속 사용하실 수 있습니다!
                  </p>
                  <p className="text-gray-500 text-xs">
                    크레딧 추가 방법: 친구 초대, 광고 시청, 결제 등
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => {
                      if (onOpenCreditPanel) {
                        onOpenCreditPanel();
                      } else {
                        alert('크레딧 추가 기능은 곧 추가될 예정입니다.');
                      }
                    }}
                    className="flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <span className="material-icons mr-2">add_card</span>
                    크레딧 추가하기
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    let bgColor = 'bg-[#f1f3f4]';
    let textColor = 'text-gray-800';
    let alignment = 'text-left self-start';

    if (isUser) {
      bgColor = 'bg-[#e3f0ff]';
      textColor = 'text-gray-900';
      alignment = 'text-right self-end';
    } else if (isError) {
      bgColor = 'bg-red-50';
      textColor = 'text-red-700';
    } else if (isLoading || isWaiting) {
      bgColor = 'bg-yellow-50';
      textColor = 'text-yellow-700';
    }

    console.log('[ChatArea] 일반 메시지 렌더링:', { bgColor, textColor, alignment });

    return (
      <div key={msg.id} className={`px-4 py-3 rounded-lg ${bgColor} ${textColor} ${alignment} w-full max-w-2xl max-w-full box-border ${alignment === 'text-right self-end' ? 'ml-auto' : 'mr-auto'}`}>
        {isLoading || isWaiting ? (
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
            {msg.content}
          </div>
        ) : (
          <div className="whitespace-pre-wrap">{msg.content}</div>
        )}
      </div>
    );
  };

  // 메시지 전송 처리
  const handleSendMessage = (options) => {
    const { text, mode, includereasons } = options;
    if (!text || typeof text !== 'string' || !text.trim()) return;
    
    if (onTextProcessing) {
      console.log('[ChatArea] onTextProcessing', { text, mode, includereasons });
      onTextProcessing({ text, mode, includereasons });
    } else if (chat && onAddMessage) {
      // 기본 메시지 추가 (API 연동 전)
      onAddMessage(chat.id, {
        id: Date.now(),
        type: 'user',
        content: text,
        timestamp: new Date().toISOString()
      });
    }
  };

  if (!chat || !chat.messages || chat.messages.length === 0) {
    console.log('[ChatArea] 빈 채팅 상태:', { chat, hasMessages: chat?.messages?.length });
    return (
      <div className="flex-1 flex flex-col bg-[#f8f9fa] text-blue-800">
        <AppTitle />
        <div className="flex-1 flex flex-col items-center justify-center px-2 pt-2">
          {otjUser ? (
            <div className="mb-8 text-center">
              <div className="text-lg text-gray-500 mb-4">안녕하세요, {otjUser.displayName}님!</div>
              <div className="text-sm text-gray-400 mb-6">남은 크레딧: {otjUser.remainingCredits}</div>
              <div className="text-sm text-gray-500 mb-4">
                교정/교열할 텍스트를 입력하고, 아래에서 모드를 선택하세요.
              </div>
            </div>
          ) : (
            <div className="mb-8 text-center">
              <div className="text-lg text-gray-500 mb-4">오탈자닷컴에 오신 것을 환영합니다!</div>
              <div className="text-sm text-gray-500 mb-4">
                교정/교열할 텍스트를 입력하고, 아래에서 모드를 선택하세요.
              </div>
              <div className="text-xs text-gray-400">
                게스트 모드로 무료 체험 가능합니다.
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  console.log('[ChatArea] 메시지 있는 채팅 렌더링:', { 
    chatId: chat.id, 
    messageCount: chat.messages.length,
    messages: chat.messages 
  });
  
  return (
    <div className="flex-1 flex flex-col bg-[#f8f9fa] w-full max-w-full overflow-x-hidden">
      {/* 메시지 영역 */}
      <div className="flex-1 overflow-y-auto px-4 py-8 flex flex-col gap-4 w-full">
        {chat.messages.map(renderMessage)}
        <div ref={ref} />
      </div>
    </div>
  );
};

export default ChatArea; 